import type { EntryDateSource, TargetSource } from '@trading/positions-ui';
import type { AppConfig } from './config.js';

export interface OrderDoc {
    symbol: string;
    buyingPrice: number;
    orderDate: string;
    status: string;
    runIds: string[];
}

/**
 * One position as fully enriched by trading_server's `GET /api/device/positions` — mirrors
 * `DevicePositionRow` in `trading_server/src/positions/service/device/types.ts`. `avgEntryPrice`/
 * `currentPrice`/etc. are the same broker facts Alpaca's own REST call already supplies (see
 * `positions-adapter.ts`, which keeps Alpaca as the source of the live position list and
 * prices); everything else here is enrichment trading_server has already computed, so the
 * adapter passes it straight through as an override instead of recomputing it client-side.
 */
export interface DevicePosition {
    symbol: string;
    side: 'long' | 'short';
    qty: number;
    avgEntryPrice: number;
    currentPrice: number;
    marketValue: number;
    unrealizedPl: number;
    unrealizedPlPct: number;
    lastUpdated: string | null;

    entryDate: string | null;
    entryDateSource: EntryDateSource | null;
    daysHeld: number | null;
    runId: string | null;

    atr14: number | null;
    atrPct: number | null;
    prevDayClose: number | null;

    stopLevel: number | null;
    stopLossBreached: boolean | null;

    target: number | null;
    targetSource: TargetSource | null;
    upsidePct: number | null;
    upsideRemainingPct: number | null;
    estimatedDays: number | null;
    daysToTarget: number | null;
    daysOverdue: number | null;

    holdingTimeFlagged: boolean;
    deadTradeFlagged: boolean;
}

export interface DevicePositionsResponse {
    positions: DevicePosition[];
    fromCache: boolean;
    cacheDate?: string;
    error?: string;
}

interface OrdersResponse {
    orders: OrderDoc[];
}

interface UpsideResponse {
    results: Record<string, number>;
}

const REQUEST_TIMEOUT_MS = 3000;

export const getOrders = async (cfg: AppConfig): Promise<OrderDoc[]> => {
    const data = await fetchWithFallback<OrdersResponse>(
        cfg,
        `/orders?userId=${encodeURIComponent(cfg.userId)}`,
    );
    return data.orders;
};

export const getUpsideByRunIds = async (
    cfg: AppConfig,
    runIds: string[],
): Promise<Record<string, number>> => {
    if (runIds.length === 0) {
        return {};
    }

    const query = runIds.map(encodeURIComponent).join(',');
    const data = await fetchWithFallback<UpsideResponse>(cfg, `/upside?runIds=${query}`);
    return data.results;
};

export const getDevicePositions = async (
    cfg: AppConfig,
    mode?: string,
): Promise<DevicePositionsResponse> => {
    const query = mode ? `?mode=${encodeURIComponent(mode)}` : '';
    return fetchWithFallback<DevicePositionsResponse>(cfg, `/positions${query}`);
};

// Tries the primary API first; on any thrown error (network failure, timeout,
// abort) or a non-2xx response, retries the same request against the
// fallback API. If there is no fallback configured, the primary's error is
// surfaced directly. If both fail, throws a single error describing both
// failures.
const fetchWithFallback = async <T>(cfg: AppConfig, pathWithQuery: string): Promise<T> => {
    try {
        return await fetchJson<T>(cfg.primaryApiUrl, pathWithQuery, cfg.apiKey);
    } catch (primaryError) {
        if (cfg.fallbackApiUrl === '') {
            throw primaryError;
        }

        try {
            return await fetchJson<T>(cfg.fallbackApiUrl, pathWithQuery, cfg.apiKey);
        } catch (fallbackError) {
            throw new Error(
                `Both primary and fallback API requests failed. Primary: ${describeError(primaryError)}. Fallback: ${describeError(fallbackError)}.`,
            );
        }
    }
};

const fetchJson = async <T>(baseUrl: string, pathWithQuery: string, apiKey: string): Promise<T> => {
    const response = await fetch(`${baseUrl}${pathWithQuery}`, {
        headers: { 'x-api-key': apiKey },
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
    });

    if (!response.ok) {
        throw new Error(
            `Request to ${baseUrl}${pathWithQuery} failed: ${response.status} ${response.statusText}`,
        );
    }

    return (await response.json()) as T;
};

const describeError = (error: unknown): string =>
    error instanceof Error ? error.message : String(error);
